module Client.IllyAdmin.Actions.Loggers.Grid

open Client
open Shared
open Shared.Collections
open Elmish
open Fable.React
open Fable.React.Props
open Shared.IllyAdmin.Loggers
open Client.IllyAdmin.Actions.Types
open Illy.SafeStack.Client
open SafeStack.Client

type Model =
    {
        Loading: bool
        Search: Text.Model
        DebouncedSearchString: string option Debounce.Model
        ApplicationSections: Section.Model list
        Overlay: Overlay.Model option
    }

type Msg =
    | SearchStringMsg of Text.Msg
    | DebounceMsg of string option Debounce.Msg
    | FilterSections of string option
    | LoadedData of LoggerMetadata list
    | ApiError of ApiError
    | SectionMsg of Section.SectionId * Section.Msg
    | OverlayMsg of Overlay.Msg

module Calls =
    let getData () =
        Api.secureCall<ILoggersIllyAdminApi,_,_,_> SecuredRoute.IllyAdmin (fun api -> api.GetMetadata) () LoadedData ApiError

module Init =
    let search value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "logger-search" "Search String" (Some "Type here to search") 200.0 false value

    let debounce () =
        Debounce.init (System.TimeSpan.FromMilliseconds 300.0) None

let init () =
    let applicationSkeletons = [
        Section.init "section1" "" [ for _ in 0 .. 3 -> LoggerMetadata.empty ]
        Section.init "section2" "" [ for _ in 0 .. 2 -> LoggerMetadata.empty ]
    ]

    {
        Loading = true
        DebouncedSearchString = Init.debounce ()
        Search = Init.search None
        ApplicationSections = applicationSkeletons
        Overlay = None
    }, Calls.getData ()

module Dispatch =
    let filterSections searchString model =
        { model with
            ApplicationSections = model.ApplicationSections |> List.map (Section.filterRows searchString)
        }, SideEffect.none

    let debounceMsg msg model =
        let m,cmd =
            Debounce.updateWithCmd
                msg
                DebounceMsg
                model.DebouncedSearchString
                (fun x -> { model with DebouncedSearchString = x })
                (FilterSections >> Cmd.ofMsg)

        m, cmd |> SideEffect.Cmd

    let sectionMsg section msg model =
        let s,eff =
            ListItem.dispatchMsgInList
                section
                (fun (s: Section.Model) -> s.Id)
                model.ApplicationSections
                Section.update
                SideEffect.none
                msg

        match eff with
        | SideEffect.Cmd cmd ->
            { model with ApplicationSections = s }, cmd |> Cmd.map (fun c -> SectionMsg (section, c)) |> SideEffect.Cmd 
        | SideEffect.Event (Section.Events.OpenOverlay selectedLoggers) ->
            let overlay =
                selectedLoggers
                |> NonEmptyList.ofList
                |> Option.map Overlay.init

            { model with Overlay = overlay }, SideEffect.none

    let overlayMsg msg model =
        match model.Overlay with
        | Some overlay ->
            let m, eff = Overlay.update msg overlay
            match eff with
            | SideEffect.Cmd cmd ->
                { model with Overlay = Some m }, cmd |> Cmd.map OverlayMsg |> SideEffect.Cmd
            | SideEffect.Event event ->
                match event with
                | Overlay.Effect.ApiError err ->
                    { model with Overlay = Some m }, err |> AppError.ApiError |> ActionEvent.AppError |> SideEffect.Event
                | Overlay.Effect.CloseOverlay ->
                    { model with Overlay = None }, SideEffect.none
        | None ->
            model, SideEffect.none

let update msg model =
    match msg with
    | SearchStringMsg cMsg ->
        let m, cmd =  Text.update cMsg model.Search
        { model with Search = m }, Cmd.batch [ cmd; Debounce.inputCmd (Text.toString m) DebounceMsg ] |> SideEffect.Cmd
    | FilterSections sMsg -> Dispatch.filterSections sMsg model
    | DebounceMsg dMsg -> Dispatch.debounceMsg dMsg model
    | LoadedData data ->
        { model with
            Loading = false
            ApplicationSections =
                data
                |> List.groupBy (fun m -> m.Application)
                |> List.map (fun (application, data) -> Section.init (application.Replace(' ', '-')) application data)
        }, SideEffect.none
    | ApiError err ->
        model, err |> AppError.ApiError |> ActionEvent.AppError |> SideEffect.Event
    | SectionMsg (section, msg) -> Dispatch.sectionMsg section msg model
    | OverlayMsg msg -> Dispatch.overlayMsg msg model

let view model dispatch =
    [
        yield div [ Id "logger-metadata" ] [
            yield Text.textInput model.Loading model.Search (SearchStringMsg >> dispatch)
            yield div [] []
            yield! model.ApplicationSections
                |> List.map (fun section -> Section.view model.Loading section ((fun v -> section.Id,v) >> SectionMsg >> dispatch))
                |> List.concat
            ]

        yield! jQueryUI.conditionallyRenderView model.Overlay Overlay.view OverlayMsg dispatch
    ]

let metaData =
    AdminActionMeta.create
        AdminActionKey.BrowseLoggers
        "Browse Loggers"
        "Lists all the available loggers in the system, and provides automatic config file generation"
        ActionGroup.Config